/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { emailTemplatesAPI } from '../../api/crm'
import { useEntityTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabel } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useEntityFields } from '../../components/automation/useEntityFields'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import EmptyState from '../../components/common/EmptyState'
import { useNotify } from '../../components/common/NotificationProvider'

/** {{placeholder}} menu for a template: record title, the sender, and the type's fields. */
const Placeholders = ({ entityType, onInsert }) => {
  const { fields } = useEntityFields(entityType || null)
  const [value, setValue] = useState('')
  const options = [['record.title', 'Record title'], ['me.name', 'My name'], ['me.email', 'My email'], ...fields.map((f) => [f.fieldKey, f.label])]
  return (
    <TextField select size="small" label="Insert" value={value} sx={{ width: 200 }} onChange={(e) => { onInsert(`{{${e.target.value}}}`); setValue('') }}>
      {options.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
    </TextField>
  )
}

const TemplateDialog = ({ template, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { entityTypes } = useEntityTypes()
  const [v, setV] = useState(() => template || { name: '', entityType: '', subject: '', body: '' })
  const set = (patch) => setV((x) => ({ ...x, ...patch }))
  const save = useMutation({
    mutationFn: () => (template ? emailTemplatesAPI.update(template.id, v) : emailTemplatesAPI.create(v)),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'email-templates'] }); notify('Template saved'); onClose() },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{template ? 'Edit template' : 'New template'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Name" required value={v.name} onChange={(e) => set({ name: e.target.value })} sx={{ flex: 1 }} inputProps={{ maxLength: 100 }} />
            <TextField select label="For" value={v.entityType || ''} onChange={(e) => set({ entityType: e.target.value })} sx={{ width: { xs: '100%', sm: 220 } }}>
              <MenuItem value="">Any record type</MenuItem>
              {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabel(t)}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField label="Subject" required fullWidth value={v.subject} onChange={(e) => set({ subject: e.target.value })} inputProps={{ maxLength: 300 }} />
            <Placeholders entityType={v.entityType} onInsert={(p) => set({ subject: v.subject + p })} />
          </Stack>
          <TextField label="Message" required multiline minRows={10} value={v.body} onChange={(e) => set({ body: e.target.value })} inputProps={{ maxLength: 20000 }} />
          <Box><Placeholders entityType={v.entityType} onInsert={(p) => set({ body: v.body + p })} /></Box>
          <Typography variant="caption" color="text.secondary">Placeholders are filled in from the record when someone picks the template; they can still edit before sending.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || !v.name.trim() || !v.subject.trim() || !v.body.trim()} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Admins: saved emails everyone can start from in the record email dialog. */
const EmailTemplatesPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const { data: list = [] } = useQuery({ queryKey: [tenantId, 'email-templates', 'all'], queryFn: () => emailTemplatesAPI.list() })
  const remove = useMutation({ mutationFn: emailTemplatesAPI.remove, onSuccess: () => { setRemoving(null); queryClient.invalidateQueries({ queryKey: [tenantId, 'email-templates'] }) } })
  return (
    <Box sx={{ maxWidth: 960 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4">Email templates</Typography>
          <Typography variant="body2" color="text.secondary">Ready-made emails your team can send from records, filled in with each record’s details.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ flexShrink: 0, whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }} onClick={() => setEditing('new')}>New template</Button>
      </Stack>
      {list.length === 0 && <Paper variant="outlined"><EmptyState title="No templates yet" description="E.g. a welcome email, a quote cover note, a document checklist." /></Paper>}
      <Stack spacing={1.5}>
        {list.map((t) => (
          <Paper key={t.id} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>{t.name}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{t.entityType ? entityLabel(t.entityType) : 'Any record type'} · {t.subject}</Typography>
            </Box>
            <Tooltip title="Edit"><IconButton onClick={() => setEditing(t)} aria-label={`Edit ${t.name}`}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton onClick={() => setRemoving(t)} aria-label={`Delete ${t.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
          </Paper>
        ))}
      </Stack>
      {editing && <TemplateDialog template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={!!removing} title="Delete template?" message={`“${removing?.name}” will no longer be offered.`} confirmLabel="Delete" destructive
        busy={remove.isPending} onConfirm={() => remove.mutate(removing.id)} onClose={() => setRemoving(null)} />
    </Box>
  )
}

export default EmailTemplatesPage
